// /frontend/src/pages/checkout/OrderConfirmationPage.jsx - Updated with Proposal Generator
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import CheckoutSteps from './CheckoutSteps';
import ProposalGenerator from '../../components/rfq/ProposalGenerator';
import EnhancedTimeline from '../../components/rfq/EnhancedTimeline';
import { FaCreditCard, FaMoneyCheck, FaUniversity, FaCheckCircle, FaPrint, FaEnvelope, FaUserCircle, FaFileDownload, FaCalendarAlt } from 'react-icons/fa';
import { orderService } from '../../services/orderService';
import { useCheckout } from '../../contexts/CheckoutContext';
import { isRFQModeEnabled, shouldHidePrices, getRFQTexts, getNotificationConfig } from '../../config/rfqConfig';

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { clearCheckoutData } = useCheckout();
  
  // State for order data
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary'); // 'summary', 'timeline', 'proposal'
  
  // RFQ Configuration
  const isRFQMode = isRFQModeEnabled();
  const hidePrices = shouldHidePrices();
  const rfqTexts = getRFQTexts();
  const notificationConfig = getNotificationConfig();
  
  // Determine if this is an RFQ order
  const isRFQOrder = isRFQMode || (order?.order?.totalPrice === 0) || (order?.totalPrice === 0);
  
  // Fetch order details
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const fetchOrder = async () => {
        try {
          setLoading(true);
          
          const orderData = await orderService.getOrderById(orderId);
          console.log('Order data received in component:', orderData);
          
          setOrder(orderData.data || orderData);
          clearCheckoutData();
          setLoading(false);
        } catch (err) {
          console.error('Error fetching order:', err);
          setError(err.message || 'There was a problem fetching your order details.');
          setLoading(false);
        }
      };
    
    fetchOrder();
  }, [orderId, isAuthenticated, navigate, clearCheckoutData]);

  // Convert order data to cart format for ProposalGenerator
  const getCartDataForProposal = () => {
    if (!order?.order?.orderItems) return { items: [] };
    
    return {
      items: order.order.orderItems.map(item => ({
        _id: item._id || `item-${Math.random()}`,
        product: {
          _id: item.product || item._id,
          name: item.name,
          images: item.image ? [item.image] : [],
          isOnSale: false,
          price: item.price || 0,
          salePrice: item.price || 0
        },
        quantity: item.quantity,
        variants: item.variants || {}
      }))
    };
  };
  
  // Format payment method for display
  const getPaymentMethodDisplay = () => {
    if (!order) return null;
    
    if (isRFQOrder) {
      return {
        icon: <FaCheckCircle className="text-blue-600" />,
        name: 'Quote Request',
        details: 'Payment information will be provided with quote'
      };
    }
    
    switch (order.paymentMethod) {
      case 'credit_card':
        return {
          icon: <FaCreditCard className="text-teal-800" />,
          name: 'Credit Card',
          details: 'Credit Card Payment'
        };
      case 'cheque':
        return {
          icon: <FaMoneyCheck className="text-teal-800" />,
          name: 'Cheque',
          details: 'Cheque Payment'
        };
      case 'wire_transfer':
        return {
          icon: <FaUniversity className="text-teal-800" />,
          name: 'Wire Transfer',
          details: 'Wire Transfer'
        };
      default:
        return {
          icon: <FaCheckCircle className="text-teal-800" />,
          name: 'Other',
          details: 'Payment details'
        };
    }
  };
  
  // Generate next steps based on RFQ or payment method
  const getNextSteps = () => {
    if (!order) return null;
    
    if (isRFQOrder) {
      return (
        <div className="space-y-2">
          <p className="font-medium">{rfqTexts.nextStepsTitle}</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Our team will review your quote request within {notificationConfig.estimatedResponseTime}</li>
            <li>You'll receive a detailed quotation via email at your registered address</li>
            <li>Review the quote and contact us if you have any questions</li>
            <li>Once you approve the quote, we'll send payment instructions</li>
            <li>After payment confirmation, we'll process and ship your order</li>
          </ol>
          <div className="mt-4 p-3 bg-yellow-50 rounded">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This is a quote request only. No payment is required at this time.
              For questions, contact us at <strong>{notificationConfig.contactEmail}</strong>
            </p>
          </div>
        </div>
      );
    }
    
    // Original payment method logic for non-RFQ orders
    switch (order.paymentMethod) {
      case 'credit_card':
        return (
          <div className="space-y-2">
            <p className="font-medium">Next Steps:</p>
            <p>Your payment is being processed. We'll send you an email once your order is confirmed.</p>
            <p>You can track your order status in your account dashboard.</p>
          </div>
        );
      case 'cheque':
        return (
          <div className="space-y-2">
            <p className="font-medium">Next Steps:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Make your cheque payable to "Henderson Design Group"</li>
              <li>Write your order number <span className="font-medium">{order._id}</span> on the back of the cheque</li>
              <li>Mail your cheque to: <span className="font-medium">123 Design Street, Suite 100, New York, NY 10001</span></li>
              <li>Your order will be processed once your cheque has cleared</li>
            </ol>
          </div>
        );
      case 'wire_transfer':
        return (
          <div className="space-y-2">
            <p className="font-medium">Next Steps:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Make a wire transfer to our bank account:</li>
              <ul className="list-disc list-inside ml-5 space-y-1">
                <li>Bank Name: First National Bank</li>
                <li>Account Name: Henderson Design Group</li>
                <li>Account Number: 1234567890</li>
                <li>Routing Number: 987654321</li>
                <li>SWIFT/BIC: FNBGUS123</li>
              </ul>
              <li>Include your order number <span className="font-medium">{order._id}</span> in the reference</li>
              <li>Your order will be processed once we receive your payment</li>
            </ol>
          </div>
        );
      default:
        return (
          <div className="space-y-2">
            <p className="font-medium">Next Steps:</p>
            <p>We'll process your order and send you an email with tracking information once it ships.</p>
          </div>
        );
    }
  };
  
  // Handle print order
  const handlePrintOrder = () => {
    window.print();
  };
  
  // Handle email receipt
  const handleEmailReceipt = async () => {
    try {
      await orderService.sendOrderConfirmationEmail(orderId);
      alert(`${isRFQOrder ? 'Quote request confirmation' : 'Order confirmation'} has been sent to your email.`);
    } catch (err) {
      alert('Failed to send email. Please try again later.');
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <CheckoutSteps step="confirmation" orderId={orderId} />
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-48 bg-gray-200 rounded mb-6"></div>
            <div className="h-32 bg-gray-200 rounded w-1/3 ml-auto"></div>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (error) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <CheckoutSteps step="confirmation" orderId={orderId} />
          <div className="bg-red-100 text-red-700 p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p>{error}</p>
            <div className="mt-4">
              <Link to="/cart" className="text-teal-800 hover:text-teal-900 font-medium">
                Return to Cart
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!order) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <CheckoutSteps step="confirmation" orderId={orderId} />
          <div className="text-center py-12">
            <p>{isRFQOrder ? 'Quote request not found.' : 'Order not found.'}</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <CheckoutSteps step="confirmation" orderId={orderId} />
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className={`${isRFQOrder ? 'bg-blue-600' : 'bg-teal-800'} text-white p-6`}>
            <div className="flex items-center justify-center">
              <FaCheckCircle className="text-3xl mr-3" />
              <h1 className="text-2xl font-bold">
                {isRFQOrder ? rfqTexts.confirmationTitle : 'Order Received!'}
              </h1>
            </div>
            <p className="text-center mt-2">
              {isRFQOrder 
                ? 'Thank you for your quote request.' 
                : 'Thank you for your order.'
              }
            </p>
          </div>
          
          {/* RFQ Success Message */}
          {isRFQOrder && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 m-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    {notificationConfig.confirmationMessage}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Tab Navigation */}
          {isRFQOrder && (
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('summary')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'summary'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Order Summary
                </button>
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'timeline'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <FaCalendarAlt className="inline mr-2" />
                  Project Timeline
                </button>
                <button
                  onClick={() => setActiveTab('proposal')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'proposal'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <FaFileDownload className="inline mr-2" />
                  Proposal & Documents
                </button>
              </nav>
            </div>
          )}
          
          <div className="p-6">
            {/* Summary Tab Content */}
            {(!isRFQOrder || activeTab === 'summary') && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                  {/* Order/Quote Info */}
                  <div className="space-y-3">
                    <h2 className="font-bold text-gray-800">
                      {isRFQOrder ? 'Quote Request Information' : 'Order Information'}
                    </h2>
                    <p><span className="font-medium">
                      {isRFQOrder ? 'Request Number:' : 'Order Number:'}
                    </span> {order._id}</p>
                    <p><span className="font-medium">Date:</span> {new Date(order.createdAt).toLocaleDateString()}</p>
                    <p><span className="font-medium">Status:</span> 
                      <span className="capitalize ml-1">
                        {isRFQOrder ? 'Quote Pending' : order.status}
                      </span>
                    </p>
                    
                    <div className="flex items-center mt-1">
                      <span className="font-medium mr-2">
                        {isRFQOrder ? 'Request Type:' : 'Payment Method:'}
                      </span>
                      {getPaymentMethodDisplay()?.icon}
                      <span className="ml-1">{getPaymentMethodDisplay()?.name}</span>
                    </div>
                  </div>
                  
                  {/* Customer Info */}
                  <div className="space-y-3">
                    <h2 className="font-bold text-gray-800">Shipping Information</h2>
                    <p className="font-medium">{order.order.shippingAddress.fullName}</p>
                    <p>{order.order.shippingAddress.address}</p>
                    <p>{order.order.shippingAddress.city}, {order.order.shippingAddress.state} {order.order.shippingAddress.postalCode}</p>
                    <p>{order.order.shippingAddress.country}</p>
                    <p>Phone: {order.order.shippingAddress.phone}</p>
                  </div>
                </div>
                
                {/* Order Items Table */}
                <div className="mb-6">
                  <h2 className="font-bold text-gray-800 mb-3">
                    {isRFQOrder ? 'Items Requested for Quote' : 'Order Summary'}
                  </h2>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Product
                          </th>
                          {!hidePrices && (
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Price
                            </th>
                          )}
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          {!hidePrices && (
                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Total
                            </th>
                          )}
                          {isRFQOrder && (
                            <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Quote Status
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {order.order.orderItems.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-12 w-12 bg-gray-100 rounded overflow-hidden mr-3">
                                  {item.image ? (
                                    <img 
                                      src={item.image}
                                      alt={item.name}
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/placeholder-image.jpg';
                                      }}
                                    />
                                  ) : (
                                    <div className="h-full w-full flex items-center justify-center text-gray-400">
                                      No Image
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                  {item.variants && Object.keys(item.variants).length > 0 && (
                                    <div className="text-xs text-gray-500">
                                      {Object.entries(item.variants).map(([key, value]) => (
                                        <div key={key}>{key}: {value}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            {!hidePrices && (
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {isRFQOrder ? 'Quote Required' : `${item.price.toFixed(2)}`}
                              </td>
                            )}
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {item.quantity}
                            </td>
                            {!hidePrices && (
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                {isRFQOrder ? 'TBD' : `${(item.price * item.quantity).toFixed(2)}`}
                              </td>
                            )}
                            {isRFQOrder && (
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Order Totals Section */}
                <div className="flex justify-end mb-6">
                  <div className="w-full sm:w-64">
                    {!isRFQOrder ? (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Subtotal:</span>
                          <span className="font-medium">${order.order.itemsPrice.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Shipping:</span>
                          <span className="font-medium">
                            {order.shippingPrice === 0 ? 'Free' : `${order.order.shippingPrice.toFixed(2)}`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tax:</span>
                          <span className="font-medium">${order.order.taxPrice.toFixed(2)}</span>
                        </div>
                        <div className="pt-2 border-t border-gray-200 flex justify-between font-bold">
                          <span>Total:</span>
                          <span>${order.order.totalPrice.toFixed(2)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <h3 className="font-medium text-blue-800 mb-2">Quote Request Summary</h3>
                          <p className="text-blue-700 text-sm mb-3">
                            Your request for quotation has been submitted successfully. 
                            Our team will review your requirements and send you a detailed quote.
                          </p>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">Items Requested:</span>
                          <span className="font-medium">{order.order.orderItems.length}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Quantity:</span>
                          <span className="font-medium">
                            {order.order.orderItems.reduce((total, item) => total + item.quantity, 0)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">Request Status:</span>
                          <span className="font-medium text-orange-600">Pending Review</span>
                        </div>
                        
                        <div className="pt-2 border-t border-gray-200 flex justify-between font-bold">
                          <span>Quote:</span>
                          <span className="text-blue-600">To Be Provided</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Next Steps */}
                <div className={`${isRFQOrder ? 'bg-blue-50' : 'bg-blue-50'} p-4 rounded-lg mb-6`}>
                  {getNextSteps()}
                </div>
              </>
            )}

            {/* Timeline Tab Content */}
            {isRFQOrder && activeTab === 'timeline' && (
              <EnhancedTimeline 
                cart={getCartDataForProposal()} 
                className="mt-0"
              />
            )}

            {/* Proposal Tab Content */}
            {isRFQOrder && activeTab === 'proposal' && (
              <ProposalGenerator 
                cart={getCartDataForProposal()}
                userInfo={user}
                quoteRequestId={orderId}
              />
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8 pt-6 border-t border-gray-200">
              <button 
                onClick={handlePrintOrder}
                className="flex items-center justify-center py-2 px-4 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
              >
                <FaPrint className="mr-2" />
                {isRFQOrder ? 'Print Quote Request' : 'Print Order'}
              </button>
              
              <button 
                onClick={handleEmailReceipt}
                className="flex items-center justify-center py-2 px-4 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                <FaEnvelope className="mr-2" />
                {isRFQOrder ? 'Email Quote Request' : 'Email Receipt'}
              </button>
              
              <Link to="/account/orders" className="flex items-center justify-center py-2 px-4 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors">
                <FaUserCircle className="mr-2" />
                {isRFQOrder ? 'My Quote Requests' : 'My Orders'}
              </Link>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mt-8">
          <Link 
            to="/browse" 
            className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
        
        {/* Conditional Payment Button - Hidden for RFQ */}
        {!isRFQOrder && (
          <div className="flex justify-center mt-4">
            <Link 
              to={`/payment/${orderId}`} 
              className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
            >
              Proceed to Payment
            </Link>
          </div>
        )}
        
        {/* RFQ Additional Information */}
        {isRFQOrder && (
          <div className="flex justify-center mt-4">
            <div className="text-center max-w-md">
              <p className="text-gray-600 mb-4">
                Your quote request has been submitted. You'll receive a quotation via email within {notificationConfig.estimatedResponseTime}.
              </p>
              <p className="text-sm text-gray-500">
                Questions? Contact us at <strong>{notificationConfig.contactEmail}</strong>
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrderConfirmationPage;